import { useCallback, useState } from 'react'
import type { ChatUiActionData, MapCameraPosition, SetRightPanelViewActionData } from 'tripPlanner/api/types'
import type { RightPanelView } from './useRightPanelState'

interface UseAICameraStateParams {
  setRightPanelView: React.Dispatch<React.SetStateAction<RightPanelView>>
}

interface UseAICameraStateReturn {
  aiCamera: MapCameraPosition | undefined
  aiCameraKey: number
  moveMapTo: (lat: number, lng: number, zoom: number) => void
  applyUiAction: (data: ChatUiActionData) => void
  handleAiCameraClear: () => void
  resetCameraState: () => void
}

function resolveAiRightPanelView(currentView: RightPanelView, requestedView: SetRightPanelViewActionData['view']): RightPanelView {
  if (currentView === 'cart') return currentView
  return requestedView
}

export function useAICameraState({
  setRightPanelView,
}: UseAICameraStateParams): UseAICameraStateReturn {
  const [aiCamera, setAiCamera] = useState<MapCameraPosition | undefined>(undefined)
  const [aiCameraKey, setAiCameraKey] = useState(0)

  const moveMapTo = useCallback((lat: number, lng: number, zoom: number) => {
    setAiCamera({ lat, lng, zoom })
    setAiCameraKey(prev => prev + 1)
  }, [])

  const applyUiAction = useCallback((data: ChatUiActionData) => {
    if (data.action !== 'set_right_panel_view') return
    setRightPanelView(prev => resolveAiRightPanelView(prev, data.view))
    if (typeof data.lat === 'number' &&
      typeof data.lng === 'number' &&
      typeof data.zoom === 'number') {
      moveMapTo(data.lat, data.lng, data.zoom)
    }
  }, [setRightPanelView, moveMapTo])

  const handleAiCameraClear = useCallback(() => {
    setAiCamera(undefined)
  }, [])

  const resetCameraState = useCallback(() => {
    setAiCamera(undefined)
    setAiCameraKey(0)
  }, [])

  return {
    aiCamera,
    aiCameraKey,
    moveMapTo,
    applyUiAction,
    handleAiCameraClear,
    resetCameraState,
  }
}
